import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import PlanoMultiplicadoresTable, { resolverPlanoAtivo } from '../../../../src/components/PlanoMultiplicadoresTable';
import PlanoParceiroFields from '../../../../src/components/PlanoParceiroFields';
import PlanoComercialInternoFields from '../../../../src/components/PlanoComercialInternoFields';
import CondicaoEspecialFields from '../../../../src/components/CondicaoEspecialFields';
import { getPlano, fidelidadeOpcoesDoPlano, carenciaOpcoesCom, isCarenciaSolicitada, resolverModalidadePlano, type ModalidadePlano } from '../../../../src/lib/politicaComercial';
import { REGRA_BONIFICACAO } from '../../../../src/lib/bonificacaoParceiro';

const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

interface PlanDetails {
    desconto_tarifa: number;
    desconto_bandeira: number;
    carencia: string;
    fidelidade: string;
    desconto_adicional: string;
    /** Código do plano da política comercial (ex: 22_50_bandeira, fora_da_politica) — usado para snapshot na negociação e na régua de aprovação. */
    plano_codigo?: string;
}

interface StepPlanProps {
    data: PlanDetails;
    onChange: (field: string, value: any) => void;
    onFinish: () => void;
    onBack: () => void;
    loading?: boolean;
    hasNextStep?: boolean;
    /** Modelo do plano escolhido no assistente (gerente sem função escolhe); sem isto, o do criador. */
    modalidade?: ModalidadePlano;
    /** Presente só para quem pode escolher o modelo na criação (podeEscolherModalidadeNaCriacao). */
    onChangeModalidade?: (m: ModalidadePlano) => void;
    /** Soma do consumo das UCs cadastradas no passo anterior — classifica o público da política. */
    volumeKwh?: number;
}

const StepPlan: React.FC<StepPlanProps> = ({ data, onChange, onFinish, onBack, loading, hasNextStep, volumeKwh, modalidade: modalidadeEscolhida, onChangeModalidade }) => {
    const { profile } = useAuth();
    // Criação: a negociação ainda não existe, então quem está na tela É o criador —
    // é este cargo que fica carimbado em plano_modalidade e define o formulário
    // para todo mundo que abrir a negociação depois (NegotiationWizard).
    const modalidade = modalidadeEscolhida ?? resolverModalidadePlano(null, profile);
    const isParceiro = modalidade === 'parceiro';
    const isVendedorInterno = modalidade === 'vendedor_interno';
    // Percentuais manuais só aparecem quando o vendedor escolhe "Fora da política"
    const [foraDaPolitica, setForaDaPolitica] = useState(
        () => !!resolverPlanoAtivo(data.desconto_tarifa, data.desconto_bandeira)?.foraDaPolitica
    );

    // Parceiro e Comercial Interno não geram snapshot de plano/multiplicador:
    // limpa o plano_codigo default do wizard. Parceiro começa no plano padrão da
    // bonificação (20%), não nos 22% do plano default do vendedor externo — só
    // nessa primeira vez (plano_codigo ainda é o default), para não desfazer a
    // escolha de quem volta a este passo.
    useEffect(() => {
        if ((isParceiro || isVendedorInterno) && data.plano_codigo) {
            onChange('plano_codigo', '');
            if (isParceiro) onChange('desconto_tarifa', REGRA_BONIFICACAO.padrao);
        }
    }, [isParceiro, isVendedorInterno]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onFinish();
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 animate-fade-in">
            <div className="text-center mb-2">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100">Definição do Plano</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">Escolha o plano da política comercial para as UCs cadastradas.</p>
            </div>

            <div className="space-y-4">
                {onChangeModalidade && (
                    <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-3 space-y-2">
                        <p className="text-xs font-semibold text-gray-600 dark:text-slate-300">Modelo do plano — escolha pelo modelo de quem vai fechar a negociação</p>
                        <div className="grid grid-cols-2 gap-2">
                            {([
                                { id: 'vendedor_interno' as ModalidadePlano, titulo: 'Closer e BDR', sub: 'desconto direto (tarifa × bandeira)' },
                                { id: 'vendedor' as ModalidadePlano, titulo: 'Vendedor externo', sub: 'planos por público (Hunter)' },
                            ]).map(op => (
                                <button
                                    key={op.id}
                                    type="button"
                                    onClick={() => {
                                        onChangeModalidade(op.id);
                                        // O modelo externo precisa de um plano da política; o interno limpa sozinho.
                                        if (op.id === 'vendedor' && !data.plano_codigo) {
                                            onChange('plano_codigo', '22_50_bandeira');
                                            onChange('desconto_tarifa', 22);
                                            onChange('desconto_bandeira', 50);
                                        }
                                    }}
                                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${modalidade === op.id
                                        ? 'border-primary bg-primary/5 text-primary font-bold'
                                        : 'border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                                >
                                    {op.titulo}
                                    <span className="block text-[11px] font-normal text-gray-500 dark:text-slate-400">{op.sub}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {isParceiro ? (
                    <PlanoParceiroFields
                        descTarifa={data.desconto_tarifa}
                        descBandeira={data.desconto_bandeira}
                        onChangeDescTarifa={v => onChange('desconto_tarifa', v)}
                        onChangeDescBandeira={v => onChange('desconto_bandeira', v)}
                        volumeKwh={volumeKwh}
                        fidelidade={data.fidelidade}
                        onChangeFidelidade={v => onChange('fidelidade', v)}
                        carencia={data.carencia}
                        onChangeCarencia={v => onChange('carencia', v)}
                    />
                ) : isVendedorInterno ? (
                    <PlanoComercialInternoFields
                        descTarifa={data.desconto_tarifa}
                        descBandeira={data.desconto_bandeira}
                        onChangeDescTarifa={v => onChange('desconto_tarifa', v)}
                        onChangeDescBandeira={v => onChange('desconto_bandeira', v)}
                        fidelidade={data.fidelidade}
                        onChangeFidelidade={v => onChange('fidelidade', v)}
                        carencia={data.carencia}
                        onChangeCarencia={v => onChange('carencia', v)}
                    />
                ) : (
                <>
                <PlanoMultiplicadoresTable
                    descTarifa={data.desconto_tarifa}
                    descBandeira={data.desconto_bandeira}
                    volumeKwh={volumeKwh}
                    fidelidade={data.fidelidade}
                    foraSelecionado={foraDaPolitica}
                    onSelectPlano={plano => {
                        onChange('plano_codigo', plano.codigo);
                        if (plano.foraDaPolitica) {
                            setForaDaPolitica(true);
                            return;
                        }
                        setForaDaPolitica(false);
                        onChange('desconto_tarifa', plano.descTarifa);
                        onChange('desconto_bandeira', plano.descBandeira);
                    }}
                />

                <CondicaoEspecialFields
                    ativo={foraDaPolitica}
                    descTarifa={data.desconto_tarifa}
                    descBandeira={data.desconto_bandeira}
                    onChangeDescTarifa={v => onChange('desconto_tarifa', v)}
                    onChangeDescBandeira={v => onChange('desconto_bandeira', v)}
                    volumeKwh={volumeKwh}
                    fidelidade={data.fidelidade}
                    onAtivar={(t, b) => {
                        setForaDaPolitica(true);
                        onChange('plano_codigo', 'fora_da_politica');
                        onChange('desconto_tarifa', t);
                        onChange('desconto_bandeira', b);
                    }}
                    onCancelar={() => {
                        const padrao = getPlano('22_50_bandeira')!;
                        setForaDaPolitica(false);
                        onChange('plano_codigo', padrao.codigo);
                        onChange('desconto_tarifa', padrao.descTarifa);
                        onChange('desconto_bandeira', padrao.descBandeira);
                    }}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Fidelidade <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(fator de ajuste)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {/* Fatores resolvidos pelo plano escolhido — no 20/50 o prazo não altera o fator */}
                        {fidelidadeOpcoesDoPlano(data.plano_codigo).map(o => {
                            const selected = data.fidelidade === o.valor;
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChange('fidelidade', o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all flex items-center justify-between gap-2 ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                    <span className="text-[11px] font-mono font-bold text-gray-500 dark:text-slate-400">{fmtMult(o.fator)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Carência <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(para solicitações)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {carenciaOpcoesCom(data.carencia).map(o => {
                            const selected = data.carencia === o.valor || (o.valor === 'sem carência' && !isCarenciaSolicitada(data.carencia));
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChange('carencia', o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {isCarenciaSolicitada(data.carencia) && (
                        <p className="flex items-center gap-1.5 text-[11px] text-orange-600 dark:text-orange-400 mt-2">
                            <AlertTriangle size={12} className="shrink-0" />
                            Carência exige aprovação do gerente ou admin — a solicitação será enviada ao concluir.
                        </p>
                    )}
                </div>
                </>
                )}

                {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                            Desconto adicional
                        </label>
                        <input
                            type="text"
                            value={data.desconto_adicional || ''}
                            onChange={(e) => onChange('desconto_adicional', e.target.value)}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                            placeholder="Ex: Condição promocional no primeiro mês..."
                        />
                    </div>
                )}
            </div>

            <div className="flex justify-between mt-6">
                <button
                    type="button"
                    onClick={onBack}
                    className="px-6 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                    Voltar
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 flex items-center gap-2"
                >
                    {loading ? 'Salvando...' : (hasNextStep ? <>Próximo <ArrowRight size={18} /></> : <>Concluir <Check size={18} /></>)}
                </button>
            </div>
        </form>
    );
};

export default StepPlan;
